(async () => {
    const { Core } = await importModule('Core');
    const PREFIX_BUY_WITH = 'buy_with_';
    const LINE_ITEM_PROPS_REGEXP = /(?<=\[).+?(?=\])/;

    /* NOTE 2026-09-01: the "Complete your setup" rows no longer post through this
       form. Each row now carries its own quantity box and Add button and adds
       itself directly (see snippets/product-buy-with.liquid and
       assets/product-to-cart.js), so no buy_with_* inputs are rendered any more.
       The handling below is left intact because it is theme code and costs
       nothing when those inputs are absent. */

    /* TCC 2026-09-04 — PAIRS-ONLY QUANTITIES.
       This form is submitted by JS, so the browser never validates the quantity
       input's own min/step: typing 3 into a pairs-only box added 3.

       TWO DOM FACTS make this fiddlier than it looks, both found the hard way:

       1. The quantity input is NOT inside <form>. It sits elsewhere in the markup
          and is associated by the HTML `form="<id>"` attribute (set in
          snippets/product-qty-selector.liquid). So `form.contains(input)` is
          false and a change listener on the form NEVER fires — the listener has
          to be on document. FormData still picks the input up, which is why the
          value reached the cart at all.
       2. `form.id` does NOT return the id string. The form contains an input
          named "id" (the variant id) and named access shadows the property, so
          `form.id` is an element. Use getAttribute('id').

       The step is read live off the input rather than cached, because the variant
       listener re-renders the quantity control whenever the variant changes. */
    customElements.define('product-form', class extends Core {

        elements = {
            formTargeter: '[data-product-form-targeter]',
            toCartButton: 'product-to-cart'
        }
        _submitHandlerRef = this._submitHandler.bind(this)
        _qtyChangeHandlerRef = this._qtyChangeHandler.bind(this)

        render() {
            if (!this.$formTargeter) return; 
            this.$form = this.$formTargeter.form;
            this.$form?.addEventListener('submit', this._submitHandlerRef);
            document.addEventListener('change', this._qtyChangeHandlerRef);
        }

        _submitHandler(e) {
            e.preventDefault();
            this.$toCartButton.addToCart(this.cartItems, this.mainVariantId);
        }

        get formId() {
            return this.$form?.getAttribute('id') || '';
        }

        get $qtyInput() {
            const id = this.formId;
            return (id && document.querySelector('[name="quantity"][form="' + id + '"]'))
                || this.$form?.querySelector('[name="quantity"]')
                || null;
        }

        _ownsInput($input) {
            if (!$input || $input.name !== 'quantity') return false;
            const id = this.formId;
            const belongs = $input.getAttribute('form');
            if (belongs) return belongs === id;
            return this.$form ? this.$form.contains($input) : false;
        }

        _qtyChangeHandler(e) {
            const $input = e.target;
            if (!this._ownsInput($input)) return;
            const snapped = this._snapQty($input.value, $input);
            if (String(snapped) !== String($input.value)) $input.value = snapped;
        }

        _snapQty(value, $input) {
            const step = Number($input?.getAttribute('step')) || 1;
            const min = Number($input?.getAttribute('min')) || 1;
            let n = Number(value);
            if (!Number.isFinite(n) || n <= 0) return min;
            if (step > 1) n = Math.round(n / step) * step;
            else n = Math.floor(n);
            return n < min ? min : n;
        }

        _isVariantInput([ inputName, value ]) {
            if (inputName === 'selling_plan') this._setSellingPlan(value);
            return inputName.includes(PREFIX_BUY_WITH) || inputName === 'id';
        }
        
        _setSellingPlan(id) {
            this.sellingPlanId = id;
        }

        _setCartItem([ inputName, value ]) {
            if (inputName === 'id') this.mainVariantId = value;
            const sellingPlanId = this.sellingPlanId || '';
            return {
                id: value,
                quantity: this._getItemQty(inputName),
                selling_plan: inputName === 'id' ? sellingPlanId : '',
                properties: this._getProperties(inputName)
            }
        }

        _getItemQty(inputName) {
            return inputName === 'id' ? this.qty : 1;
        }
        _getProperties(inputName) {
            return inputName === 'id' ? this.properties : {};
        }

        get cartItems() {
            return Array.from(this.formData.entries())
                .filter(this._isVariantInput.bind(this))
                .map(this._setCartItem.bind(this))
        }

        get formData() {
            return new FormData(this.$form);
        }

        get qty() {
            return this._snapQty(this.formData.get('quantity'), this.$qtyInput);
        }
        get properties() {
            return Array.from(this.formData.entries()).reduce((acc, [key, val]) => {
                if (key.startsWith('properties[') && key.endsWith(']') && val) {
                    const propKey = key.match(LINE_ITEM_PROPS_REGEXP)[0];
                    acc[propKey] = val;
                }
                return acc;
            }, {});
        }

        disconnect() {
            this.$form?.removeEventListener('submit', this._submitHandlerRef);
            document.removeEventListener('change', this._qtyChangeHandlerRef);
        }
    })
})();
